import dayjs from "dayjs";

import { UnitApplicationRequest } from "../../types/unit";
import {
    IAdditionalInformationFormData,
    IBeneficialOwnersFormData,
    IBusinessInformationFormData,
    IOfficerInformationFormData,
} from "./FormData";
import { IWelcomeFormData } from "./WelcomePage";

export const createUnitApplicationPayload = (
    welcomeInfo: IWelcomeFormData,
    businessInfo: IBusinessInformationFormData,
    officerInfo: IOfficerInformationFormData,
    beneficialOwnerInfo: IBeneficialOwnersFormData,
    additionalInformationData: IAdditionalInformationFormData | undefined,
    idempotencyKey: string,
    iovationValue?: string,
): UnitApplicationRequest => {
    return {
        name: businessInfo.name,
        address: {
            street: businessInfo.addressStreet.trim(),
            street2: businessInfo.addressStreet2.trim(),
            city: businessInfo.addressCity.trim(),
            state: businessInfo.addressDistrict.trim(),
            // Unit requires postal codes even for countries that don't use them.
            // We make it optional, and just fill it with "-" before we submit.
            // See: https://github.com/levrofin/afdc/issues/1183#issuecomment-1042547344
            postalCode: businessInfo.addressZipcode.trim() || "-",
            country: businessInfo.addressCountry.trim(),
        },
        phone: {
            countryCode: businessInfo.phoneCountryCode,
            number: businessInfo.phoneNumber,
        },
        dba: businessInfo.dba || "",
        stateOfIncorporation: businessInfo.stateOfIncorporation,
        ein: businessInfo.ein?.replace(/-/g, "") || "111111111",
        entityType: businessInfo.entityType,
        businessVertical: businessInfo.businessVertical,
        yearOfIncorporation: businessInfo.yearOfIncorporation.getFullYear().toString(),
        website: businessInfo.website,
        annualRevenue: businessInfo.annualRevenue,
        numberOfEmployees: businessInfo.numberOfEmployees,
        cashFlow: businessInfo.cashFlow,
        countriesOfOperation: businessInfo.countriesOfOperation,
        contact: {
            fullName: {
                first: officerInfo.bcFirstName,
                last: officerInfo.bcLastName,
            },
            email: officerInfo.bcEmail,
            phone: {
                countryCode: officerInfo.bcPhoneCountryCode,
                number: officerInfo.bcPhoneNumber,
            },
        },
        officer: {
            fullName: {
                first: officerInfo.firstName,
                last: officerInfo.lastName,
            },
            dateOfBirth: dayjs(officerInfo.dob).format("YYYY-MM-DD"),
            title: officerInfo.title,
            ssn: officerInfo.ssn?.replace(/-/g, "") || "",
            passport: officerInfo.passportNumber || "",
            nationality: officerInfo.nationality || "",
            email: officerInfo.email,
            phone: {
                countryCode: officerInfo.phoneCountryCode,
                number: officerInfo.phoneNumber,
            },
            address: {
                street: officerInfo.addressStreet.trim(),
                street2: officerInfo.addressStreet2.trim(),
                city: officerInfo.addressCity.trim(),
                state: officerInfo.addressDistrict.trim(),
                postalCode: officerInfo.addressZipcode.trim() || "-",
                country: officerInfo.addressCountry.trim(),
            },
            occupation: additionalInformationData?.officer.occupation,
            annualIncome: additionalInformationData?.officer.annualIncome,
            sourceOfIncome: additionalInformationData?.officer.sourceOfIncome,
        },
        beneficialOwners: beneficialOwnerInfo.beneficialOwner.map((owner, idx) => ({
            fullName: {
                first: owner.firstName,
                last: owner.lastName,
            },
            dateOfBirth: dayjs(owner.dob).format("YYYY-MM-DD"),
            ssn: owner.ssn?.replace(/-/g, "") || "",
            passport: owner.passportNumber || "",
            nationality: owner.nationality || "",
            email: owner.email,
            percentage: owner.percentage ? Math.round(owner.percentage) : 0,
            phone: {
                countryCode: owner.phoneCountryCode,
                number: owner.phoneNumber,
            },
            address: {
                street: owner.addressStreet.trim(),
                street2: owner.addressStreet2.trim(),
                city: owner.addressCity.trim(),
                state: owner.addressDistrict.trim(),
                postalCode: owner.addressZipcode.trim() || "-",
                country: owner.addressCountry.trim(),
            },
            occupation: owner.isOfficer
                ? additionalInformationData?.officer.occupation
                : additionalInformationData?.beneficialOwners[idx].occupation,
            annualIncome: owner.isOfficer
                ? additionalInformationData?.officer.annualIncome
                : additionalInformationData?.beneficialOwners[idx].annualIncome,
            sourceOfIncome: owner.isOfficer
                ? additionalInformationData?.officer.sourceOfIncome
                : additionalInformationData?.beneficialOwners[idx].sourceOfIncome,
        })),
        idempotencyKey,
        deviceFingerprints: iovationValue ? [{ provider: "iovation", value: iovationValue }] : undefined,
        tags: {
            promo_code: welcomeInfo.tags.promo_code || undefined,
        },
    };
};
